import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PublicQuoteBrand } from '@bandido/shared/types';

function mapLink(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : undefined;
  } catch {
    return undefined;
  }
}

/** Published identity only: later agency edits must not change this information. */
export function PublicQuoteAgency({ brand }: { brand: PublicQuoteBrand }) {
  const { t } = useTranslation();
  const tr = (key: string) => t(`commercialQuotes.presentation.${key}`);
  const info = brand.presentation;
  if (
    ![
      brand.email,
      brand.address,
      brand.legalName,
      info?.hours,
      info?.registration,
      info?.paymentMethods,
      info?.terms,
    ].some((v) => v?.trim()) &&
    !info?.branches?.length
  )
    return null;
  return (
    <Paper
      component="section"
      variant="outlined"
      sx={{ mt: 4, p: { xs: 2, md: 3 }, borderRadius: 3, overflowWrap: 'anywhere' }}
    >
      <Stack spacing={2}>
        <Typography variant="h6">{brand.name}</Typography>
        {brand.legalName?.trim() && brand.legalName !== brand.name && <Typography>{brand.legalName}</Typography>}
        {brand.address?.trim() && <Typography>{brand.address}</Typography>}
        {brand.email?.trim() && <Link href={`mailto:${encodeURIComponent(brand.email.trim())}`}>{brand.email}</Link>}
        {(['hours', 'registration', 'paymentMethods'] as const).map(
          (key) =>
            info?.[key]?.trim() && (
              <Box key={key}>
                <Typography fontWeight={600}>{tr(key)}</Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>{info[key]}</Typography>
              </Box>
            ),
        )}
        {!!info?.branches?.length && (
          <Box>
            <Typography fontWeight={600}>{tr('branches')}</Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {info.branches.map((branch, index) => (
                <Box key={index}>
                  <Typography fontWeight={500}>{branch.name}</Typography>
                  <Typography>{branch.address}</Typography>
                  {mapLink(branch.mapUrl) && (
                    <Link href={mapLink(branch.mapUrl)} target="_blank" rel="noreferrer">
                      {t('commercialQuotes.publicView.map')}
                    </Link>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        {info?.terms?.trim() && (
          <Box component="details">
            <Box component="summary" sx={{ cursor: 'pointer', py: 1, fontWeight: 600 }}>
              {tr('terms')}
            </Box>
            <Typography sx={{ whiteSpace: 'pre-line', mt: 1 }}>{info.terms}</Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
